import type {
  OptionsItemType,
  RegionSelectValueType,
} from '@/pages/home/components/NewRegionSelect/type';
import { SELECTED_OPTION_CHECKBOX } from '@/pages/home/components/NewRegionSelect/config';
import type { IColumnInfo, IColumnRawInfo } from '@dbl/core/dataSource';
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import type { CustomColumn } from '@/pages/datalab/pages/Multidimensional/Analysis/interface';
import { FieldTypeIcon } from '@dbl/core/dataSource';
import enMultiLocale from '@/pages/datalab/_locales/en-US/multidimensional';
import zhMultiLocale from '@/pages/datalab/_locales/zh-CN/multidimensional';
import type { TBatchAddTargetKey, IBatchAddLevel2Node } from '../types';
import { buildFieldTree } from './buildFieldTree';
import {
  type IBatchFieldDisableContext,
  type TBatchFieldDisableReason,
  evaluateBatchFieldDisable,
} from './batchFieldDisableRules';
import { columnInfoToFieldKey, getFieldDragIdentity } from './fieldIdentity';
import { isSensitiveFieldBlocked } from './isSensitiveFieldBlocked';

/** disable 原因 → NewRegionSelect 选项 bubble 文案（中英文） */
const DISABLE_REASON_BUBBLE: Record<
TBatchFieldDisableReason,
{ bubbleContent: string; bubbleContentCN: string; }
> = {
  sensitive: {
    bubbleContent: enMultiLocale.dbl_multi_batch_add_sensitive_disabled,
    bubbleContentCN: zhMultiLocale.dbl_multi_batch_add_sensitive_disabled,
  },
  xaxis_date_only: {
    bubbleContent: enMultiLocale.dbl_multi_batch_add_xaxis_date_only,
    bubbleContentCN: zhMultiLocale.dbl_multi_batch_add_xaxis_date_only,
  },
  show_dimension_no_datetime: {
    bubbleContent: enMultiLocale.dbl_multi_batch_add_show_dimension_no_datetime,
    bubbleContentCN: zhMultiLocale.dbl_multi_batch_add_show_dimension_no_datetime,
  },
  chart_limit: {
    bubbleContent: enMultiLocale.dbl_multi_batch_add_limit_disabled,
    bubbleContentCN: zhMultiLocale.dbl_multi_batch_add_limit_disabled,
  },
  trend_dimension_date_first: {
    bubbleContent: enMultiLocale.dbl_multi_batch_add_trend_dimension_invalid,
    bubbleContentCN: zhMultiLocale.dbl_multi_batch_add_trend_dimension_invalid,
  },
  trend_dimension_invalid_first: {
    bubbleContent: enMultiLocale.dbl_multi_batch_add_trend_dimension_invalid,
    bubbleContentCN: zhMultiLocale.dbl_multi_batch_add_trend_dimension_invalid,
  },
};

/** 与 menuTab FieldClassIconNameMap 一致：维度/指标分组标题 icon */
const FIELD_CLASS_ICON_NAME_MAP: Record<string, string> = {
  维度: 'gear-m',
  指标: 'port-path-icon',
};

/** 维度/指标分组标题 SvgIcon；自定义字段分组无组头 icon */
const getGroupHeaderSvgIcon = (group: IBatchAddLevel2Node): string | undefined => {
  if (group.type === 'customField' || !group.label) {
    return undefined;
  }
  return FIELD_CLASS_ICON_NAME_MAP[group.label];
};

/** 与左侧字段列表 menuTab 一致，column_type → SvgIcon type */
const getFieldTypeSvgIcon = (columnType?: string): string =>
  FieldTypeIcon[columnType || ''] || 'abc';

/** 单字段 → 右侧可选项；disabled 与 bubble 由 evaluateBatchFieldDisable 统一产出 */
const mapFieldToOption = (
  field: IColumnRawInfo,
  disableContext: IBatchFieldDisableContext,
): OptionsItemType => {
  const fieldKey = getFieldDragIdentity(field);
  const disableReason = evaluateBatchFieldDisable(field, disableContext);
  // disableReason 非 null 即不可选；已勾选项 evaluate 返回 null，故仍可取消
  const isDisabled = disableReason !== null;
  return {
    value: fieldKey,
    text:
      field.column_display_en ||
      field.column_display ||
      field.column_name,
    textCN: field.column_display || field.column_name,
    svgIconType: getFieldTypeSvgIcon(field.column_type),
    disabled: isDisabled,
    ...(disableReason ? DISABLE_REASON_BUBBLE[disableReason] : {}),
  };
};

export interface IBuildRegionSelectOptionsParams {
  dataTables: IGetTableInfoDto[];
  customColumns: CustomColumn[];
  targetKey: TBatchAddTargetKey;
  isEn: boolean;
  customFieldLabel: string;
  chartType?: string;
  /** 传入时按当前勾选态动态计算 disabled（数量上限、Trend 首维等） */
  disableContext: IBatchFieldDisableContext;
}

/** 将字段树转为 NewRegionSelect 所需的 options 结构（一级=表，二级=字段） */
export const buildRegionSelectOptions = (
  params: IBuildRegionSelectOptionsParams,
): OptionsItemType[] => {
  const {
    dataTables,
    customColumns,
    targetKey,
    isEn,
    customFieldLabel,
    disableContext,
  } = params;

  const tree = buildFieldTree(
    dataTables,
    customColumns,
    isEn,
    customFieldLabel,
  );

  return tree.map(level1 => ({
    value: level1.id,
    text: level1.label,
    textCN: level1.label,
    children: level1.children.flatMap(group =>
      group.fields.map(field => {
        const child = mapFieldToOption(field, disableContext);
        if (group.type !== 'customField' && group.label) {
          child.groupKey = group.id;
          // groupText / groupTextCN 分别供英文/中文环境展示，与 menuTab name_en / name 对齐
          child.groupText = group.labelEn || group.label;
          child.groupTextCN = group.label;
          // 组头 icon 与 menuTab bd-group 一致，字段行仍用 column_type icon
          const groupHeaderSvgIconType = getGroupHeaderSvgIcon(group);
          if (groupHeaderSvgIconType) {
            child.groupHeaderSvgIconType = groupHeaderSvgIconType;
          }
        }
        return child;
      }),
    ),
  }));
};

/** 从 existingColumns 生成下拉初始勾选；敏感字段在禁止落区不进入 markets */
export const buildRegionSelectValueFromColumns = (
  existingColumns: IColumnInfo[],
  targetKey?: TBatchAddTargetKey,
): RegionSelectValueType => ({
  type: SELECTED_OPTION_CHECKBOX,
  regions: [],
  markets: (existingColumns || [])
    // targetKey 未传时不做敏感过滤；传入时与 isSensitiveFieldBlocked 口径一致
    .filter(column => !targetKey || !isSensitiveFieldBlocked(targetKey, column))
    .map(columnInfoToFieldKey),
});

/** NewRegionSelect 一级 tab 列表，与 options 顶层 value 对应 */
export const getRealRegionsList = (options: OptionsItemType[]): string[] =>
  options.map(item => item.value);

/**
 * 解析 NewRegionSelect 左侧默认高亮的一级项（对应 defaultSelectedRegion）。
 * - 无勾选：取 options 第一项，保证右侧字段列表默认展开；
 * - 有勾选：按左侧一级顺序，取首个「children 中含已勾选 field」的节点（非 markets[0] 反查）。
 */
export const resolveDefaultSelectedRegion = (
  options: OptionsItemType[],
  markets: string[] = [],
): string | undefined => {
  if (markets.length > 0) {
    const selectedMarketSet = new Set(markets);
    const matchedOption = options.find(option =>
      (option.children || []).some(
        child => !child.isGroupHeader && selectedMarketSet.has(child.value),
      ),
    );
    if (matchedOption) {
      return matchedOption.value;
    }
  }
  return options[0]?.value;
};

